import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { BookOpen, Award, UserCheck, Briefcase, Phone, Mail, Loader2, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FormationsPage = () => {
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistName, setWaitlistName] = useState('');
  const [waitlistFormationType, setWaitlistFormationType] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    if (!waitlistEmail) {
      toast.error('Veuillez entrer votre adresse courriel');
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/leads-formations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: waitlistEmail, name: waitlistName, formation_type: waitlistFormationType }),
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Erreur');
      }
      toast.success('Inscription confirmée!');
      setIsSubscribed(true);
    } catch (error) {
      toast.error(error.message || 'Une erreur est survenue.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-16" data-testid="formations-page">
      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-center justify-center bg-[#1a1a1a] overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-display font-bold text-white mb-6">
            Formations
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto">
            Développez vos compétences. Propulsez votre carrière.
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xl text-gray-700 leading-relaxed mb-6">
            Chez Nexor Talents, nous croyons que le développement professionnel ne s'arrête pas au recrutement.
          </p>
          <p className="text-xl text-gray-700 leading-relaxed">
            Nos formations sont conçues pour renforcer vos compétences, affiner votre posture professionnelle et vous donner un avantage concret sur le marché.
          </p>
        </div>
      </section>

      {/* Nexi & Élyor - Transmission */}
      <section className="py-8 bg-white">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <img
            src="/images/mascots/transmission.png"
            alt="Élyor transmet son savoir à Nexi"
            className="w-full max-w-lg mx-auto"
          />
        </div>
      </section>

      {/* Formation Themes */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-16 text-center">
            Nos thèmes de formation
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {/* Expertise Assurance */}
            <Card className="border-0 shadow-xl hover:shadow-2xl transition-all">
              <CardContent className="p-10">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gold/10 mb-6">
                  <Award className="w-10 h-10 text-gold" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Expertise Assurance
                </h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Des formations spécialisées pour les professionnels de l'assurance de dommages qui souhaitent approfondir leur expertise et rester conformes aux exigences du secteur.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-gold mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Déontologie et conformité professionnelle</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-gold mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Gestion de notes et documentation rigoureuse</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-gold mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Meilleures pratiques du secteur</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Coaching Carrière */}
            <Card className="border-0 shadow-xl hover:shadow-2xl transition-all">
              <CardContent className="p-10">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gold/10 mb-6">
                  <UserCheck className="w-10 h-10 text-gold" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Coaching Carrière
                </h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Un accompagnement personnalisé pour vous préparer à décrocher le poste qui vous correspond et à vous démarquer dans un marché compétitif.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-gold mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Préparation aux entrevues (simulation et rétroaction)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-gold mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Optimisation du CV et du positionnement professionnel</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-gold mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Développement des soft skills et de la maturité professionnelle</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Waitlist */}
      <section className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-0 shadow-2xl">
            <CardContent className="p-8 md:p-12">
              {isSubscribed ? (
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100 mb-6">
                    <CheckCircle2 className="w-12 h-12 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Merci pour votre inscription!</h3>
                  <p className="text-gray-600">Nous vous contacterons dès que nos formations seront disponibles.</p>
                </div>
              ) : (
                <>
                  <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gold/10 mb-4">
                      <BookOpen className="w-8 h-8 text-gold" />
                    </div>
                    <h3 className="text-3xl font-bold text-gray-900 mb-4">
                      Inscrivez-vous à la liste d'attente
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      Nos formations arrivent bientôt. Soyez parmi les premiers informés.
                    </p>
                  </div>

                  <form onSubmit={handleWaitlistSubmit} className="space-y-4">
                    <div>
                      <Label htmlFor="waitlistName" className="text-base">Nom complet</Label>
                      <Input
                        id="waitlistName"
                        value={waitlistName}
                        onChange={(e) => setWaitlistName(e.target.value)}
                        placeholder="Votre nom"
                        className="mt-2 h-12"
                      />
                    </div>
                    <div>
                      <Label htmlFor="waitlistEmail" className="text-base">Adresse courriel <span className="text-red-500">*</span></Label>
                      <Input
                        id="waitlistEmail"
                        type="email"
                        value={waitlistEmail}
                        onChange={(e) => setWaitlistEmail(e.target.value)}
                        placeholder="votre@courriel.com"
                        required
                        className="mt-2 h-12"
                      />
                    </div>
                    <div>
                      <Label htmlFor="waitlistFormationType" className="text-base">Type de formation recherchée</Label>
                      <Input
                        id="waitlistFormationType"
                        value={waitlistFormationType}
                        onChange={(e) => setWaitlistFormationType(e.target.value)}
                        placeholder="Ex. : déontologie, coaching entrevue, gestion de notes..."
                        className="mt-2 h-12"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      data-testid="waitlist-submit"
                      className="w-full bg-gold hover:bg-[#9a7109] text-white text-lg py-6 font-semibold disabled:opacity-70"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Inscription...
                        </>
                      ) : (
                        "M'inscrire à la liste d'attente"
                      )}
                    </Button>
                  </form>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Mandats sur mesure */}
      <section className="py-24 bg-[#1a1a1a] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Briefcase className="w-12 h-12 text-gold mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-display font-bold mb-6">
            Mandats sur mesure
          </h2>
          <p className="text-xl text-gray-300 leading-relaxed mb-8 max-w-3xl mx-auto">
            Vous avez des besoins spécifiques en formation pour votre équipe ? Nous concevons des programmes adaptés à votre réalité, vos objectifs et votre secteur d'activité.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:5145453383" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gold hover:bg-[#9a7109] text-white font-semibold rounded-lg transition-colors">
              <Phone className="w-4 h-4" />
              514-545-3383
            </a>
            <a href="mailto:info@nexortalents.com" className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-gold text-gold hover:bg-gold hover:text-white font-semibold rounded-lg transition-colors">
              <Mail className="w-4 h-4" />
              info@nexortalents.com
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FormationsPage;
